import { IInfoProvidingControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { XGAction } from "UI/UI/UI/XGControls/Actions/XGAction";
import { ControlInfo, IControlInfoObject } from "UI/UI/UI/XGControls/ControlInfo";
import { setExtendedPropertiesOnElement } from "UI/utils";
import { Key } from "../../../Global/Base/Utils/EventUtils";
import { StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../UI.BaseControls/Control";
import { ElementUtils } from "../../UI.ElementarControls/Helpers/ElementUtils";
import { registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { Icon } from "../../UI/UI/FormUI/FormUI";
import { IPrimary } from "../../UI/UI/XGControls/Actions/IPrimary";
import { Action } from "../Actions/Action";
import { IAction, IWFAction } from "../Actions/IAction";
import { MenuType } from "../Actions/MenuType";
import { IDropDownMenu, IDropDownMenuItem } from "./DropDownInterfaces";

export class DropDownMenuItem extends Control implements IDropDownMenuItem {
    protected _icon: string;
    protected _action: IAction;
    private _filterLoc: string;

    protected _locKey: string;
    protected _locParams: string[];

    protected _iconElement: HTMLElement;
    protected _captionElement: HTMLElement;
    protected _textElement: HTMLElement;

    public priority: number;
    protected _parent: IDropDownMenu;
    public get parent(): IDropDownMenu {
        return this._parent;
    }
    public set parent(value: IDropDownMenu) {
        this._parent = value;
        if (this.action instanceof IPrimary && this.action.isPrimary && this._parent) {
            this._parent.hasPrimaryAction = true;
        }
    }
    public isOpened: boolean;
    public noCloseAfterSelect: boolean;

    public get locKey(): string {
        return this._locKey;
    }
    public set locKey(v: string) {
        this._locKey = v;
    }

    public get locParams(): string[] {
        return this._locParams;
    }
    public set locParams(v: string[]) {
        this._locParams = v;
    }

    public get icon(): string {
        return this._icon;
    }

    public set icon(newIcon: string) {
        this._setIcon(newIcon, true);
    }

    public get filterLoc(): string {
        return this._filterLoc;
    }

    public set filterLoc(v: string) {
        this._filterLoc = v;
    }

    public get action(): IAction {
        return this._action;
    }

    public set action(action: IAction) {
        this._action = action;
        if (this.action) {
            this.priority = action.priority;
            this.visible = action.visible;
            this.icon = typeof action.icon === "number" ? Icon[action.icon] : action.icon;
            this.classList.toggle("disabled", !action.isActive);

            this.action.visibilityChanged.add((_sender, value) => this.visible = value);
            this.action.isActiveChanged.add((_screen, value) => {
                this.classList.toggle("disabled", !value);
            });

            if (this.action.locKey) {
                const originalWidth = this.clientWidth;
                Localization.addLocDataAsync(this._captionElement, this.action.locKey, undefined, undefined, this.action.locParams, true)
                    .then(el => this._processNewCaption(el.textContent, originalWidth));
            } else if (action instanceof IWFAction && action.caption && action.caption.length > 0) {
                this._caption = action.caption;
            }

            if (this.action.id) {
                this.id = `${this.action.id}-da`;
            }

            if (this.action.extendedProperties) {
                setExtendedPropertiesOnElement(this.action.extendedProperties, this);
            }

            if (this.action.toMenu != null) {
                this.setAttribute("data-tomenu", MenuType[this.action.toMenu]);
            }
        }
    }

    protected set _caption(value: string) {
        const width = this.clientWidth;

        this._captionElement.textContent = value;
        this._captionElement.title = value;

        this._processNewCaption(value, width);
    }

    private _processNewCaption(value: string, originalWidth: number): void {
        this.filterLoc = StringUtils.removeAccents(value.toLowerCase());
        // pokud se pri nacteni retezce zmeni sirka, je nutno prepocitat pozici DropDownMenu (kvuli absolutnimu pozicovani)
        this._recalculatePositionIfNeeded(originalWidth);
    }

    protected override _constructor(): void {
        super._constructor();

        this.priority = 15;
        this._createElement();

        /**
         * Položky jsou sdílené pro více řádků záznamů. Zobrazují se ale v PopupMenu, které sdílené není a při zavření se odstraňuje z DOM.
         * Položka je v tu chvíli potomkem, takže by se disposovala také, pokud by zůstal disposeOnDetach nastavený na true.
         */
        this.disposeOnDetach = false;
    }

    protected _setIcon(newIcon: string, rewriteOriginal: boolean): void {
        if (this.icon) {
            this._iconElement.classList.remove(this._icon);
        }
        if (newIcon) {
            this._iconElement.classList.add(newIcon);
        }
        if (rewriteOriginal) {
            this._icon = newIcon;
        }
    }

    protected override connectedCallback(): void {
        super.connectedCallback();
        this._setListener();
    }

    public async setCaption(v: string, titleLocKey?: string, titleLocParams?: string[]): Promise<void> {
        this.filterLoc = StringUtils.removeAccents(v.toLowerCase());
        // Regex pro formát loc klíče (př.: GUI-468461)
        this._captionElement.textContent = /^GUI-[0-9]+$/.test(v) ? await Localization.getTextPromise(v) : v;

        if (titleLocKey) {
            Localization.addLocDescData(this._captionElement, titleLocKey, "title", titleLocParams, true);
        }
    }

    public setLocalizedCaption(locKey: string, locParams?: string[]): void {
        Localization.addLocData(this._captionElement, locKey, undefined, undefined, locParams, true);
    }

    public async getCaption(): Promise<string> {
        if (this._captionElement.textContent) {
            return this._captionElement.textContent;
        }

        return Localization.getTextPromise(this.locKey);
    }

    protected _init(element: DropDownMenuItem, icon: string, action: IAction, _locKey: string, enabled: boolean = false, priority: number = 15, _locParams?: string[]): void {
        element.action = action;
        action.enabled = enabled;
        element.priority = priority;
        this._setIconOnElement(element, action, icon);
    }

    protected _setIconOnElement(element: DropDownMenuItem, _action: IAction, icon: string): void {
        element.icon = icon;
    }

    public static createDropMenuItem(icon: string, fnc: () => Promise<void>, locKey: string, enabled: boolean = false, priority: number = 15, locParams?: string[]): DropDownMenuItem {
        return this._initDropDownElement(new DropDownMenuItem(), icon, fnc, locKey, enabled, priority, locParams);
    }

    protected static _initDropDownElement(element: DropDownMenuItem, icon: string, fnc: () => Promise<void>, locKey: string, enabled: boolean = false, priority: number = 15, locParams?: string[]): DropDownMenuItem {
        const action = new Action(fnc, locKey, locParams);
        element._init(element, icon, action, locKey, enabled, priority, locParams);
        return element;
    }

    public highlightCaption(text: string): void {
        if (text.length) {
            ElementUtils.highlightText(text, this._captionElement);
        } else {
            ElementUtils.clearHighlight(this._captionElement);
        }
    }

    protected _createElement(): void {
        this._textElement = document.createElement("div");

        this.classList.add("DropDownMenuItem");
        if (this.action) {
            this.classList.toggle("disabled", !this.action.isActive);
        }

        this._textElement.classList.add("Text");

        this._createIconElement();
        this._createCaptionElement();

        if (this._iconElement) {
            this.appendChild(this._iconElement);
        }
        this.appendChild(this._textElement);
        this._textElement.appendChild(this._captionElement);

        if (this.enabled) {
            this.tabIndex = 0;
        }
    }

    protected _createIconElement(): void {
        this._iconElement = document.createElement("div");

        this._iconElement.classList.add("Icon");

        this._iconElement.classList.add("menuIcon");
        if (this.icon) {
            this._iconElement.classList.add(this.icon);
        }
    }

    protected _createCaptionElement(): void {
        this._captionElement = document.createElement("div");
        this._captionElement.classList.add("Caption");
    }

    protected _clickHandler(event: MouseEvent | KeyboardEvent): void {
        // Zobrazení informací o akci
        if (event.ctrlKey || event.metaKey) {
            if (this.action instanceof IInfoProvidingControl) {
                this.action.getControlInfo(null, null).then((info) => {
                    ControlInfo.displayInfo((this.action as XGAction).owner.element, info as IControlInfoObject);
                });
            }
            return;
        }

        event.stopImmediatePropagation(); // EventHelper způsobuje okamžité zavření menu svou cancelled událostí

        if (!this.action.isActive) {
            return;
        }

        if (!this.noCloseAfterSelect) { // Menu je třeba pozavírat ještě přes spuštěním akce, protože ta může otevírat další menu
            this.parent.closeAll(event instanceof KeyboardEvent && event.key === Key.Enter, true);
        }

        if (typeof this.action.execute === "function") {
            this.action.execute(); // Není třeba čekat na výsledný Promise
        }
    }

    protected _setListener(): void {
        const fCloseSubmenu = () => {
            if (this.parent.openedSubmenu) {
                //this.classList.remove("selected");
                //logger.log(this.parent.openedSubmenu);
                this.parent.openedSubmenu.close();
                this.parent.openedSubmenu = null;
            }
        };

        this.registerEventListener("click", (e: MouseEvent) => {
            if (e.button === 0) {
                this._clickHandler(e);
            }
        });

        this.registerEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === Key.Space || (!(e.target instanceof HTMLButtonElement) && e.key === Key.Enter)) {
                this._clickHandler(e);
            }
        });

        this.registerEventListener("mouseenter", (e: MouseEvent) => {
            if (!this.parent || !this.parent.openedSubmenu || this.parent.openedSubmenu.disableCloseTimeout) {
                return;
            }

            if (this.parent.openedSubmenu.closeTimeout) {
                clearTimeout(this.parent.openedSubmenu.closeTimeout);
            }
            if (ElementUtils.inElement((this.parent.openedSubmenu as any) as HTMLElement, e)) {
                return;
            }
            this.parent.openedSubmenu.closeTimeout = self.setTimeout(() => {
                fCloseSubmenu();
            }, 300);
        });

        this.registerEventListener("mouseleave", () => {
            if (this.parent?.openedSubmenu?.closeTimeout) {
                clearTimeout(this.parent.openedSubmenu.closeTimeout);
            }
        });
    }

    public override remove(): void {
        if (this.parent) {
            this.parent.removeItem(this);
        }
        super.remove();
    }

    private _recalculatePositionIfNeeded(originalWidth: number): void {
        if (originalWidth !== this.clientWidth) {
            this.parent.recalculatePosition();
        }
    }

    public override dispose(): void {
        super.dispose();
        this._eventsController.removeAllListeners();
    }
}

registerCustomElement("tsc-DropDownMenuItem", DropDownMenuItem);
